import {
  FluidAnchorPosition,
  FluidButtonTranslations,
  FluidButtonType,
  FluidHorizontalPosition,
  FluidIconConfig,
  FluidPopoverConfig,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  Prop,
  Watch,
} from '@stencil/core';

import {
  propSync,
  validatePopoverProp,
} from '../../utils/logging/deprecation.utils';
import { Utils } from '../../utils/utils';

/**
 * @displayName Sticky Button
 *
 * @contextData FLUID Sticky Button is a simple component that displays a fixed-position FLUID Button on the right side of the viewport. It's primarily used when an action or navigation link needs to be visible at all times, regardless of the user's scroll position on the page.
 * <br/><br/>
 * NOTE: By default, Sticky Button is NOT responsive at this time (i.e. it's meant to be used on a SINGLE device type). However, it can be made responsive by creating logic to dynamically update the "verticalPlacement" Prop value based on viewport size.
 */
@Component({
  tag: 'fluid-sticky-button',
  styleUrl: 'fluid-sticky-button.scss',
  shadow: true,
})
export class FluidStickyButton
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  private _popoverConfig: FluidPopoverConfig;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidStickyButtonElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * The label to give the button (if no ID is supplied, the label will
   * be automatically camelCased and used as an ID for accessibility &
   * e2e testing).
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the color & style of the Button.
   */
  @Prop({ mutable: true }) type: FluidButtonType = FluidButtonType.PRIMARY;

  @Watch('type')
  onTypeChange() {
    propSync(this, 'type', 'color', true);
  }

  /**
   * When true, removes Sticky Button's default right & bottom margins.
   */
  @Prop({ mutable: true }) removeMargin = true;

  /**
   * When true, removes Sticky Button's default right margin. NOTE:
   * this Prop is overridden by "removeMargin" Prop.
   */
  @Prop({ mutable: true }) removeHorizontalMargin = false;

  /**
   * When true, removes Sticky Button's default bottom margin. NOTE:
   * this Prop is overridden by "removeMargin" Prop.
   */
  @Prop({ mutable: true }) removeVerticalMargin = false;

  /**
   * When provided, displays a Popover on :hover when Sticky Button is
   * DISABLED. NOTE: Popover will NOT be displayed on :hover UNLESS
   * component is disabled.
   */
  @Prop({ mutable: true }) popoverConfig: string | FluidPopoverConfig;

  @Watch('popoverConfig')
  onPopoverConfigChange() {
    this._popoverConfig = validatePopoverProp(
      this.host,
      this.popoverConfig,
      undefined
    );
  }

  /**
   * When true, Sticky Button will be smaller than the default size.
   */
  @Prop({ mutable: true }) small = false;

  /**
   * If the button is part of a control bar, what position is it
   * in? 'start' | 'middle' | 'end'
   */
  @Prop({ mutable: true }) controlBarPosition: FluidHorizontalPosition;

  /**
   * An additional icon to be placed before the button label.
   */
  @Prop({ mutable: true }) iconBefore: FluidIconConfig;

  /**
   * An additional icon to be placed after the button label.
   */
  @Prop({ mutable: true }) iconAfter: FluidIconConfig;

  /**
   * Sets the Sticky Button's vertical offset (in px) from the top
   * of the viewport.
   */
  @Prop({ mutable: true }) verticalPlacement: number;

  /**
   * When true, Sticky Button will be disabled. Setting this Prop to
   * true also allows the embedded Popover to be displayed on :hover
   * (i.e. Popover is DISABLED when this Prop is false or undefined).
   */
  @Prop({ mutable: true }) disabled: boolean;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: setZIndex - Set component z-index relative to global base value
   * @action: propSync - keeps deprecated "color" Prop in-sync with "type" Prop
   * @action: validatePopoverProp - map deprecated "popover" Prop to "popoverConfig"
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
    propSync(this, 'type', 'color', isNullOrUndefined(this.color));
    this._popoverConfig = validatePopoverProp(
      this.host,
      this.popoverConfig,
      this.popover
    );
  }

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidButtonTranslations;

  // ================================================================ //
  // -- Local Methods

  /**
   * Returns map of deprecated Popover Props and/or properties from
   * "popoverConfig" Prop in a common FluidPopoverConfig object used
   * to render Button's embedded Popover.
   */
  getPopoverConfig(): FluidPopoverConfig {
    if (!this._popoverConfig) return;

    return {
      header: this.popoverTitle,
      width: this.popoverWidth,
      placement: this.popoverPlacement,
      ...this._popoverConfig, // "_popoverConfig" overrides individual Popover Props
      preventPopoverOpening: !this.disabled,
      ignoreClippingBoundaries: false,
      parentContainer: this.host,
      asWrapper: true,
    };
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host
        style={{
          top: `${this.verticalPlacement}px`,
          'z-index': `${this.zIndex}`,
        }}>
        <div class={`fluid-sticky-button theme-${this.theme}`}>
          <fluid-button
            id={this.host.id}
            config={{
              label: this.label,
              fluidTranslate: this.fluidTranslate,
              type: this.type,
              removeMargin: this.removeMargin,
              removeHorizontalMargin: this.removeHorizontalMargin,
              removeVerticalMargin: this.removeVerticalMargin,
              small: this.small,
              controlBarPosition: this.controlBarPosition,
              iconBefore: this.iconBefore,
              iconAfter: this.iconAfter,
              disabled: this.disabled,
              theme: this.theme,
              overrideGlobalTheme: true,
              popoverConfig: this.getPopoverConfig(),
            }}
          />
        </div>
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index value for Sticky Button container.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "color" is now deprecated. Instead, use the "type" Prop
   * (same functionality, just a different naming convention) -- Style
   * settings applied to the Button.
   * @deprecated
   */
  @Prop({ mutable: true }) color: FluidButtonType;

  @Watch('color')
  onColorChange() {
    propSync(this, 'type', 'color');
  }

  /**
   * DEPRECATED: "popover" is now deprecated (https://chromestatus.com/feature/5463833265045504).
   * Instead, use the "message" property of the "popoverConfig" Prop. --
   * When provided, displays a Popover on :hover when Sticky Button is
   * DISABLED. This Prop sets the "message" within the Popover. NOTE:
   * Popover will NOT be displayed on :hover UNLESS component is disabled.
   * @deprecated
   */
  @Prop({ mutable: true }) popover: string;

  @Watch('popover')
  onPopoverChange() {
    if (this.popover === null) return;
    this._popoverConfig = validatePopoverProp(
      this.host,
      undefined,
      this.popover
    );
  }

  /**
   * DEPRECATED: "popoverPlacement" is now deprecated. Due to the
   * placement of Sticky Button within the viewport, the Popover's
   * placement should ALWAYS be "top". -- Sets preferred side of the
   * Sticky Button to display Popover (when open).
   * @deprecated
   */
  @Prop({ mutable: true }) popoverPlacement: FluidAnchorPosition =
    FluidAnchorPosition.TOP;

  /**
   * DEPRECATED: "popoverTitle" is now deprecated. Instead, use the
   * "header" property of the "popoverConfig" Prop. -- Adds a title to the
   * Popover. If undefined, Popover will show without a header section.
   * @deprecated
   */
  @Prop({ mutable: true }) popoverTitle: string;

  /**
   * DEPRECATED: "popoverWidth" is now deprecated. Instead, use the
   * "width" property of the "PopoverConfig" Prop. -- Specifies the maximum
   * width of the Popover (in px).
   * @deprecated
   */
  @Prop({ mutable: true }) popoverWidth: number;
}
